import { useTenantContext } from 'context/TenantContext'
import Translation from 'components/Translation'
import SearchBox from 'components/SearchBox'
import { useEffect, useState } from 'react'
import DataTable from 'components/DataTable'
import { ContentTenantApiResponse } from 'types/content'
import { postApiRequest } from 'utils/fetch-helper'
import { classNames } from 'utils/dom-helpers'
import SavingStates, { SavingStatus } from 'components/Spinners/Saving'
import { useRouter } from 'next/router'
import { useAuth } from 'context/AuthContext'
import { DEFAULT_LANGUAGE_CODE } from 'components/LanguageSelector'
import { useAppContext } from 'context/AppContext'

function RenderContent({
    filtered_content,
    preferred_languages,
    handleEdit,
}: {
    filtered_content: any[]
    preferred_languages: Record<string, string>[]
    handleEdit(
        key: string,
        value: string,
        locale: string,
        locale_id: string
    ): void
}) {
    const rows: HTMLTableRowElement[] = (filtered_content || []).map(
        (content: any) =>
            (
                <tr key={content?.key}>
                    {preferred_languages.map((lang: any, index: number) => (
                        <td
                            key={lang.code}
                            className={classNames(
                                index == 0 ? 'sticky left-0 z-20' : '',
                                'border w-72 min-w-72'
                            )}
                        >
                            <input
                                type="text"
                                className="block text-sm font-[450] py-[14px] min-w-[15rem] w-full border-none bg-white focus:ring-primary-light focus:border-primary-light"
                                value={content[lang.code]}
                                title={content[lang.code]}
                                onChange={(e) =>
                                    handleEdit(
                                        content.key,
                                        e.target.value,
                                        lang.code,
                                        lang.id
                                    )
                                }
                            />
                        </td>
                    ))}
                </tr>
            ) as unknown as HTMLTableRowElement
    )

    const headers = preferred_languages.map((lang) => {
        return {
            label: lang.text,
            classNames: classNames(
                'px-6 py-3 text-left text-sm text-gray-500 border tracking-wider font-bold bg-slate-100 sticky top-0 min-w-72 w-72',
                lang.code == DEFAULT_LANGUAGE_CODE
                    ? 'sticky left-0 z-30 '
                    : 'z-20'
            ),
        }
    })

    return (
        <div className="flex flex-col w-[80%] max-h-[40vh] align-middle relative min-w-full mt-5">
            <div className="w-full max-h-full flex overflow-auto">
                <DataTable
                    columns={headers}
                    rows={rows}
                    table_class_names="relative "
                    head_class_names="w-72"
                    body_class_names="w-full"
                />
            </div>
        </div>
    )
}

function LanguageForm({ from_settings }: { from_settings?: boolean }) {
    const router = useRouter()
    const { CustomBookingPageModal } = useAuth()
    const {
        bookingPageContent,
        getBookingPageContent,
        tenant,
        preferred_languages,
    } = useTenantContext()
    const { languages } = useAppContext()
    const [filter, setFilter] = useState('')
    const [filtered_content, setFilteredContent] = useState<any>([])
    const [timer, setTimer] = useState<any>()
    const [status, setStatus] = useState<SavingStatus>(SavingStatus.NONE)

    useEffect(() => {
        if (bookingPageContent) {
            if (filter && filter.length > 0) {
                const filter_lower_case = filter.toLowerCase()
                setFilteredContent(
                    bookingPageContent.filter((c: any) =>
                        c?.en?.toLowerCase()?.includes(filter_lower_case)
                    )
                )
            } else {
                setFilteredContent(bookingPageContent)
            }
        }
    }, [filter, bookingPageContent])

    async function handleEdit(
        key: string,
        value: string,
        locale: string,
        locale_id: string
    ) {
        setFilteredContent(
            filtered_content.map((c: any) => {
                if (c.key === key) {
                    return { ...c, [locale]: value }
                } else {
                    return c
                }
            })
        )
        if (timer) {
            clearTimeout(timer)
            setTimer(undefined)
        }
        setTimer(
            setTimeout(async () => {
                try {
                    setStatus(SavingStatus.SAVING)
                    if (!locale_id) {
                        locale_id = languages.find(
                            (lang: any) => lang?.locale == locale
                        )?.id as any
                    }
                    const payload = {
                        languageId: locale_id,
                        pageName: 'BOOKING_PAGE',
                        tagName: key,
                        tenantId: tenant?.id,
                        value: value,
                    } as ContentTenantApiResponse
                    let response = await postApiRequest(
                        '/v1/contents/tenant',
                        payload
                    )

                    if (response.ok) {
                        setStatus(SavingStatus.SAVED_SUCCESS)
                        getBookingPageContent()
                    } else {
                        setStatus(SavingStatus.SAVED_FAILURE)
                    }
                } catch (e) {
                    setStatus(SavingStatus.SAVED_FAILURE)
                }
            }, 1000)
        )
    }

    return (
        <div className="p-5 overflow-x-scroll grid grid-flow-row">
            <div className="relative">
                <Translation
                    content_key="preferred_languages_mapping"
                    className="text-primary text-lg font-medium font-display pb-[30px]"
                    render_as="h2"
                />
                <Translation
                    content_key="preferred_languages_mapping_desc"
                    className="font-[450] text-base text-gray-400"
                    render_as="p"
                />
                <Translation
                    content_key='common.refresh_to_load_translations'
                    className='font-bold text-base text-secondary-cyan'
                    render_as='p'
                />
                <button
                    className="absolute top-0 right-0 px-2 py-1 border-gray-300 border-[0.8px] rounded-lg text-center vertical-align"
                    onClick={() => {
                        router.push('/dashboard/settings/languages')
                        CustomBookingPageModal.close()
                    }}
                >
                    <Translation
                        content_key="update_preferred_languages_setting"
                        className="text-primary cursor-pointer"
                        render_as="label"
                    />
                    <i className="feather feather-external-link text-primary-light ml-3" />
                </button>
            </div>
            <div className="flex flex-row relative my-3">
                <div className="flex-auto width-2/3 max-w-[50%]">
                    <SearchBox value={filter} setValue={setFilter} />
                </div>
                <div className="flex-auto absolute right-0 text-base text-bold align-middle">
                    <Translation content_key="total_no_of_strings" />
                    &nbsp;
                    {filtered_content.length}
                </div>
            </div>
            <RenderContent
                filtered_content={filtered_content}
                preferred_languages={preferred_languages}
                handleEdit={handleEdit}
            />
            <div className="h-[90px] flex pt-6 ">
                <SavingStates status={status} />
                <button
                    type="button"
                    onClick={() => {
                        CustomBookingPageModal.close()
                    }}
                    className={classNames(
                        'border border-gray-300 rounded-lg py-1 inline-block mr-10 px-2 absolute right-0',
                        from_settings ? 'hidden' : 'visible'
                    )}
                >
                    <Translation content_key="close" />
                </button>
            </div>
        </div>
    )
}

export default LanguageForm
